import { useEffect, useRef, useState } from "react";
import apiClient from "../../api/client";

// Shows a nav badge when the user has Arcade invites in "answered" or "ready" states.
// Lightweight polling (initial + every 60s). Safe to use in GlobalNav + Dashboard nav.
export function useArcadeInviteBadge({ enabled = true, intervalMs = 60000 } = {}) {
  const [count, setCount] = useState(0);
  const timerRef = useRef(null);
  const inFlightRef = useRef(false);

  const getSeenAt = () => {
    try {
      const raw = localStorage.getItem("arcade_invites_seen_at");
      const n = raw ? Number(raw) : 0;
      return Number.isFinite(n) ? n : 0;
    } catch (_) {
      return 0;
    }
  };

  async function fetchCount() {
    if (!enabled) return;
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    try {
      const res = await apiClient.get("/arcade/invite/mine");
      const items = res?.data?.items || [];
      const seenAt = getSeenAt();
      const c = items.reduce((acc, inv) => {
        const s = inv?.status;
        if (!(s === "answered" || s === "ready")) return acc;
        const updatedAt = inv?.updated_at || inv?.updatedAt || null;
        const updatedMs = updatedAt ? Date.parse(updatedAt) : 0;
        if (seenAt && updatedMs && updatedMs <= seenAt) return acc;
        return acc + 1;
      }, 0);
      setCount(c);
    } catch (e) {
      setCount(0);
    } finally {
      inFlightRef.current = false;
    }
  }

  useEffect(() => {
    if (!enabled) {
      setCount(0);
      return;
    }
    fetchCount();
    timerRef.current = setInterval(fetchCount, intervalMs);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = null;
    };
  }, [enabled, intervalMs]); // eslint-disable-line

  // Clear badge instantly when user views invites (same tab)
  useEffect(() => {
    if (!enabled) return;
    const onSeen = () => {
      setCount(0);
    };
    window.addEventListener("arcade_invites_seen", onSeen);
    return () => window.removeEventListener("arcade_invites_seen", onSeen);
  }, [enabled]);

  return {
    count,
    hasBadge: count > 0,
    label: count > 9 ? "9+" : String(count),
    refresh: fetchCount,
  };
}
